// Spec 078 — card de um modelo na biblioteca: prévia (lazy, dentro da cerca do 3D), nome, tamanho,
// data, uso por máquinas, e as ações de gestão (calibrar, substituir, associar, excluir).
import * as React from "react";
import { Link } from "react-router-dom";
import { Link2, RefreshCw, SlidersHorizontal, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/ui/pill";
import type { Model3dSummary } from "@/types/models-3d.types";

const Thumbnail = React.lazy(() =>
  import("../CalibrationStudio/Model3dThumbnail").then((m) => ({ default: m.Model3dThumbnail }))
);

const mb = (n: number) =>
  `${(n / (1024 * 1024)).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} MB`;
const date = new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" });

export interface ModelCardProps {
  model: Model3dSummary;
  isGestor: boolean;
  webgl: boolean;
  onReplace: (m: Model3dSummary) => void;
  onAssign: (m: Model3dSummary) => void;
  onDelete: (m: Model3dSummary) => void;
}

export function ModelCard({
  model,
  isGestor,
  webgl,
  onReplace,
  onAssign,
  onDelete,
}: ModelCardProps): React.ReactElement {
  return (
    <article
      className="flex flex-col overflow-hidden rounded-xl border border-border-subtle bg-card shadow-sm"
      data-testid="model-card"
      data-model-id={model.id}
    >
      <div className="h-40 w-full bg-bg-band">
        {webgl ? (
          <React.Suspense
            fallback={
              <div className="flex h-full items-center justify-center text-xs text-text-tertiary">
                Carregando prévia…
              </div>
            }
          >
            <Thumbnail model={model} />
          </React.Suspense>
        ) : (
          <div className="flex h-full items-center justify-center text-xs text-text-tertiary">
            Prévia indisponível sem WebGL
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="truncate text-base font-semibold text-text-primary">{model.name}</h3>
          <Pill tone={model.machineCount > 0 ? "info" : "neutral"}>
            {model.machineCount === 1 ? "1 máquina" : `${model.machineCount} máquinas`}
          </Pill>
        </div>
        <p className="text-xs text-text-tertiary">
          {mb(model.fileSize)} · enviado em {date.format(new Date(model.createdAt))}
          {model.updatedAt !== model.createdAt
            ? ` · calibrado em ${date.format(new Date(model.updatedAt))}`
            : ""}
        </p>
        {isGestor ? (
          <div className="mt-auto flex flex-wrap gap-2 pt-2">
            <Button size="sm" variant="outline" asChild>
              <Link to={`/modelos-3d/${model.id}/calibrar`}>
                <SlidersHorizontal className="h-4 w-4" aria-hidden />
                Calibrar
              </Link>
            </Button>
            <Button size="sm" variant="outline" onClick={() => onAssign(model)}>
              <Link2 className="h-4 w-4" aria-hidden />
              Associar a máquinas
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onReplace(model)}
              aria-label={`Substituir arquivo de ${model.name}`}
            >
              <RefreshCw className="h-4 w-4" aria-hidden />
              Substituir
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onDelete(model)}
              aria-label={`Excluir ${model.name}`}
            >
              <Trash2 className="h-4 w-4" aria-hidden />
              Excluir
            </Button>
          </div>
        ) : null}
      </div>
    </article>
  );
}
